import { Breadcrumbs, Typography, Grid, Box, Link, Container, Card, Button, Tabs, Tab, useTheme } from '@material-ui/core';
import ChevronRightIcon from '../../icons/ChevronRight';
import PlusIcon from '../../icons/Plus';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useSettings from '../../hooks/useSettings';
import useMounted from '../../hooks/useMounted';
import { useState, useCallback, useEffect } from 'react';
import { usersApi } from '../../api/usersApi';
import UsersListTable from '../../components/dashboard/users/UsersListTable';
import CreateUserDialog from '../../components/dashboard/users/CreateUserDialog';
import useAuth from '../../hooks/useAuth';

const UsersList = () => {
  const mounted = useMounted();
  const { settings } = useSettings();
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [currentTab, setCurrentTab] = useState('active');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const user = useAuth();

  const getUsers = useCallback(async () => {
    try {
      const data = await usersApi.getUsers(window.localStorage.getItem('accessToken'));
      if (mounted.current) {
        setUsers(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [mounted]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const handleUserCreated = (newUser) => {
    setUsers([...users, newUser]);
  };

  const handleTabChange = (event, value) => {
    setCurrentTab(value);
  };

  const activeUsers = users.filter((u) => u.id_service !== 999);
  const archivedUsers = users.filter((u) => u.id_service === 999);

  return (
    <>
      <Helmet>
        <title>Tableau de bord: Liste des utilisateurs | Syndays</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 8
        }}
      >
        <Container maxWidth={settings.compact ? 'xl' : false}>
          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: theme.palette.mode === 'light' ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.05)',
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(241,245,249,0.7) 100%)'
                : 'linear-gradient(135deg, rgba(15,23,42,0.6) 0%, rgba(30,41,59,0.4) 100%)',
              backdropFilter: 'blur(20px)',
              boxShadow: theme.palette.mode === 'light'
                ? '0 4px 24px rgba(0,0,0,0.04)'
                : '0 4px 24px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
                  flexShrink: 0,
                }}
              >
                <ManageAccountsIcon sx={{ color: '#fff', fontSize: '1.7rem' }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Liste des utilisateurs
                </Typography>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                  sx={{ mt: 1 }}
                >
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}
                  >
                    Tableau de bord
                  </Link>
                  <Typography
                    color="textSecondary"
                    variant="subtitle2"
                  >
                    Utilisateurs
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>

            {user.user.id_service === 0 && user.user.access_level === 0 && (
              <Button
                startIcon={<PlusIcon fontSize="small" />}
                onClick={() => setCreateDialogOpen(true)}
                variant="contained"
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.35)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                  px: 2.5,
                  py: 1.2,
                }}
              >
                Créer un utilisateur
              </Button>
            )}
          </Box>
          <Box
            sx={{
              mt: 2,
              p: 0.6,
              display: 'inline-block',
              borderRadius: '16px',
              border: '1px solid',
              borderColor: theme.palette.mode === 'light' ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.05)',
              background: theme.palette.mode === 'light' ? '#fff' : 'rgba(30, 41, 59, 0.45)',
              boxShadow: theme.palette.mode === 'light' ? '0 4px 16px rgba(0,0,0,0.03)' : '0 4px 16px rgba(0,0,0,0.15)',
            }}
          >
            <Tabs
              onChange={handleTabChange}
              scrollButtons="auto"
              value={currentTab}
              variant="scrollable"
              sx={{
                minHeight: 44,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  minHeight: 40,
                  borderRadius: '11px',
                  mx: 0.3,
                  px: 2.5,
                  color: 'text.secondary',
                  transition: 'all 0.18s',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? 'rgba(59,130,246,0.06)' : 'rgba(255,255,255,0.04)',
                    color: '#3b82f6',
                  },
                  '&.Mui-selected': {
                    color: '#fff',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
                  },
                },
              }}
            >
              <Tab label="Actifs" value="active" />
              <Tab label="Archivés" value="archived" />
            </Tabs>
          </Box>
          {user.user.id_service === 0 && (
            <Card
              sx={{
                mt: 4,
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                overflow: 'hidden',
              }}
            >
              <UsersListTable users={currentTab === 'active' ? activeUsers : archivedUsers} />
            </Card>
          )}
          {user.user.id_service !== 0 && 'Vous n\'avez pas le droit de voir les utilisateurs'}
        </Container>
      </Box>

      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onUserCreated={handleUserCreated}
      />
    </>
  );
};

export default UsersList;
